import {useTranslation} from '@/translations/i18n'
import {useFormContext} from 'react-hook-form'
import {
    StyledNewConversationFieldGroup,
    StyledNewConversationFieldLabel,
    StyledNewConversationInputText,
    StyledNewConversationTextArea,
    StyledParticipantsDisplay,
    StyledParticipantsText
} from '../new-conversation-form/style'
import {
    SelectedItem,
    SelectedItemWithData,
    UserSelectionItem,
    UsersAndGroupsMultiSelectPopover
} from '@/features/users/components/users-and-groups-multi-select-popover/UsersAndGroupsMultiSelectPopover'
import {AvatarGroup} from '@/components/commons/avatar-group/AvatarGroup'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {CombinedItem} from '@/features/users/hooks/useUsersAndGroupsSearchableList'
import {ConversationEntitySelector} from '../conversation-entity-selector/ConversationEntitySelector'

export const NewConversationFields = ({
    selectedItems,
    handleParticipantToggle,
    currentDisplayItems,
    patientId
}: {
    selectedItems: SelectedItem[]
    handleParticipantToggle: (users: UserSelectionItem[], itemData: CombinedItem) => void
    currentDisplayItems: SelectedItemWithData[]
    // Absent/null for internal conversations: hides the patient-scoped entity selector below
    patientId?: string | null
}) => {
    const {t} = useTranslation()
    const {register} = useFormContext()

    return (
        <>
            <StyledNewConversationFieldGroup direction="column" gap={1.5} fullWidth>
                <StyledNewConversationFieldLabel>{t('conversations:title_label')}</StyledNewConversationFieldLabel>
                <StyledNewConversationInputText
                    inputSize="lg"
                    type="text"
                    placeholder={t('conversations:title_placeholder')}
                    {...register('title')}
                />
            </StyledNewConversationFieldGroup>
            <StyledNewConversationFieldGroup direction="column" gap={1.5} fullWidth>
                <StyledNewConversationFieldLabel>{t('conversations:recipients_label')}</StyledNewConversationFieldLabel>
                <UsersAndGroupsMultiSelectPopover
                    currentDisplayItems={currentDisplayItems}
                    selectedItems={selectedItems}
                    toggleClick={handleParticipantToggle}
                    trigger={
                        <StyledParticipantsDisplay type="button" variant="ghost" onMouseDown={e => e.preventDefault()}>
                            {currentDisplayItems.length > 0 ? (
                                <AvatarGroup>
                                    {currentDisplayItems.slice(0, 3).map(item => (
                                        <AvatarGroup.Item
                                            key={item.id}
                                            name={item.name}
                                            imageUrl={item.type === 'user' ? item.avatarUrl || undefined : undefined}
                                            size="sm"
                                        />
                                    ))}
                                </AvatarGroup>
                            ) : (
                                <PlusIcon size={20} />
                            )}

                            <StyledParticipantsText className={currentDisplayItems.length > 0 ? 'has-selection' : ''}>
                                {currentDisplayItems.length === 0
                                    ? t('conversations:select_recipients')
                                    : currentDisplayItems.length === 1
                                      ? currentDisplayItems[0].name
                                      : currentDisplayItems.length === 2
                                        ? `${currentDisplayItems[0].name} e ${currentDisplayItems[1].name}`
                                        : `${currentDisplayItems[0].name} e altri ${currentDisplayItems.length - 1}`}
                            </StyledParticipantsText>
                        </StyledParticipantsDisplay>
                    }
                />
                {patientId && <ConversationEntitySelector patientId={patientId} />}
            </StyledNewConversationFieldGroup>
            <StyledNewConversationFieldGroup direction="column" gap={1.5} fullWidth style={{height: '100%'}}>
                <StyledNewConversationFieldLabel>{t('conversations:message')}</StyledNewConversationFieldLabel>
                <StyledNewConversationTextArea
                    placeholder={t('conversations:message_placeholder')}
                    rows={5}
                    {...register('message')}
                />
            </StyledNewConversationFieldGroup>
        </>
    )
}
